import { useEffect, useMemo, useRef } from "react";
import date from "date-and-time";
import clsx from "clsx";

import { ITicketAttachment, ITicketNote } from "../../models/Tickets/Ticket";
import { isPublicNote, isPublicStaffNote } from "../../utils/ticketNoteReplies";
import {
  formatAttachmentSize,
  isImageAttachment,
} from "../../utils/ticketAttachments";

interface TicketChatThreadOpening {
  /** Who raised the ticket - shown above the first bubble. */
  author: string;
  subject: string;
  description?: string;
  created: string | Date;
}

interface TicketChatThreadProps {
  notes: ITicketNote[];
  /** Renders the ticket itself as the first message in the thread. */
  opening?: TicketChatThreadOpening;
  /** Placed in the thread by upload time - management stays in the attachments list. */
  attachments?: ITicketAttachment[];
  /** Object URLs from useTicketAttachments, keyed by attachment id. */
  attachmentPreviewUrls?: Record<string, string>;
  onAttachmentClick?: (attachment: ITicketAttachment) => void;
  onAttachmentDownload?: (attachment: ITicketAttachment) => void;
  /** Sizing / spacing for the scroll container - the chat styling stays here. */
  className?: string;
  emptyMessage?: string;
}

type ThreadItem =
  | { kind: "note"; at: number; note: ITicketNote }
  | { kind: "attachment"; at: number; attachment: ITicketAttachment };

const bubbleWidth =
  "w-fit max-w-[min(100%,92%)] lg:max-w-[min(100%,calc(50%+10rem))]";

const formatStamp = (value: string | Date) =>
  date.format(new Date(value), "DD/MM/YYYY HH:mm");

/**
 * Public conversation as a chat thread - customer on the left, staff on the
 * right, oldest first, with attachments dropped in at the point they were
 * uploaded. The view sticks to the latest item: jumping straight there on mount,
 * then scrolling smoothly as replies come in.
 */
export default function TicketChatThread({
  notes,
  opening,
  attachments,
  attachmentPreviewUrls,
  onAttachmentClick,
  onAttachmentDownload,
  className,
  emptyMessage = "No public replies yet.",
}: TicketChatThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnce = useRef(false);

  const items = useMemo<ThreadItem[]>(() => {
    const merged: ThreadItem[] = [
      ...notes.filter(isPublicNote).map<ThreadItem>((note) => ({
        kind: "note",
        at: new Date(note.created).getTime(),
        note,
      })),
      ...(attachments ?? []).map<ThreadItem>((attachment) => ({
        kind: "attachment",
        at: new Date(attachment.created).getTime(),
        attachment,
      })),
    ];

    return merged.sort((a, b) => a.at - b.at);
  }, [notes, attachments]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: hasScrolledOnce.current ? "smooth" : "auto",
    });
    hasScrolledOnce.current = true;
  }, [items.length]);

  const renderNote = (note: ITicketNote) => {
    const staffSide = isPublicStaffNote(note);
    const label =
      note.authorName ?? note.authorEmail ?? (staffSide ? "Staff" : "Customer");

    return (
      <div
        key={note.id}
        className={clsx(
          "flex w-full",
          staffSide ? "justify-end" : "justify-start",
        )}
      >
        <div
          className={clsx(
            bubbleWidth,
            "rounded-2xl px-3 py-2 shadow-sm",
            staffSide
              ? "bg-orange-100 text-slate-900 rounded-br-md border border-orange-200/80"
              : "bg-white text-slate-900 rounded-bl-md border border-slate-200",
          )}
        >
          <p className="text-[11px] font-medium text-slate-600 mb-1">{label}</p>
          <p className="text-sm whitespace-pre-wrap leading-snug">{note.note}</p>
          <p className="text-[10px] text-slate-500 mt-1.5 text-right">
            {formatStamp(note.created)}
          </p>
        </div>
      </div>
    );
  };

  /**
   * Attachments carry no author flag on the API, so they sit centred rather than
   * pick a side and risk labelling them as the wrong person.
   */
  const renderAttachment = (attachment: ITicketAttachment) => {
    const previewUrl = attachmentPreviewUrls?.[attachment.id];
    const isImage = isImageAttachment(attachment.contentType);

    return (
      <div key={attachment.id} className="flex w-full justify-center">
        <div className="w-fit max-w-[min(100%,80%)] rounded-2xl border border-slate-200 bg-white/80 px-3 py-2 text-center shadow-sm">
          <p className="text-[10px] uppercase tracking-wide text-slate-400">
            Attachment added
          </p>

          {isImage ? (
            <button
              type="button"
              className="mt-1.5 block rounded-lg border border-slate-200 p-1 hover:border-orange-300 disabled:cursor-default"
              disabled={!previewUrl || !onAttachmentClick}
              onClick={() => previewUrl && onAttachmentClick?.(attachment)}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={attachment.fileName}
                  className="max-h-40 max-w-[14rem] rounded object-contain"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-24 w-32 items-center justify-center rounded bg-slate-100 text-[10px] text-slate-500">
                  Loading preview…
                </div>
              )}
            </button>
          ) : null}

          <p className="mt-1 max-w-[14rem] truncate text-xs font-medium text-slate-800">
            {attachment.fileName}
          </p>
          {onAttachmentDownload ? (
            <button
              type="button"
              onClick={() => onAttachmentDownload(attachment)}
              className="text-[11px] font-medium text-orange-700 hover:text-orange-900"
            >
              Download
            </button>
          ) : null}
          <p className="text-[10px] text-slate-500">
            {formatAttachmentSize(attachment.fileSize)} ·{" "}
            {formatStamp(attachment.created)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={scrollRef}
      className={clsx(
        "overflow-y-auto rounded-xl border border-slate-200/90 bg-slate-100/90 px-2 sm:px-3 py-3 space-y-2",
        className,
      )}
    >
      {opening ? (
        <div className="flex w-full justify-start">
          <div
            className={clsx(
              bubbleWidth,
              "rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3 py-2 text-slate-900 shadow-sm",
            )}
          >
            <p className="text-[11px] font-medium text-slate-600 mb-1">
              {opening.author}
            </p>
            <p className="text-[10px] uppercase tracking-wide text-slate-400">
              Ticket opened
            </p>
            <p className="text-sm font-semibold leading-snug">
              {opening.subject}
            </p>
            {opening.description?.trim() ? (
              <p className="mt-1 text-sm whitespace-pre-wrap leading-snug text-slate-800">
                {opening.description}
              </p>
            ) : null}
            <p className="text-[10px] text-slate-500 mt-1.5 text-right">
              {formatStamp(opening.created)}
            </p>
          </div>
        </div>
      ) : null}

      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">{emptyMessage}</p>
      ) : (
        items.map((item) =>
          item.kind === "note"
            ? renderNote(item.note)
            : renderAttachment(item.attachment),
        )
      )}
    </div>
  );
}
